import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Timer, UserCheck } from "@phosphor-icons/react";
import { MapCard } from "./MapCard";
import { scrollToId } from "../lib/scroll";

const LINES = [
  { text: "People Are Finding", accent: false },
  { text: "Businesses on Google Maps.", accent: false },
  { text: "Are They Choosing Yours?", accent: true },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBg = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28"
    >
      <motion.div
        style={{ y: yBg }}
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-32 h-[34rem] w-[34rem] rounded-full bg-pine-light/50 blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="eyebrow mb-6"
          >
            Google Business Profile Optimization
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="text-5xl font-extrabold leading-[1.02] tracking-tighter text-ink sm:text-6xl md:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-pine" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 1,
                    delay: 0.25 + i * 0.14,
                    ease: [0.76, 0, 0.24, 1],
                  }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7 max-w-xl text-base text-soot sm:text-lg"
          >
            Your Google Business Profile is often the first impression customers
            get of your business. Make sure it gives them a reason to call,
            book, or visit.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-9"
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => scrollToId("#audit-form")}
              className="btn-primary"
            >
              Get My Free Google Maps Audit
              <ArrowRight weight="bold" size={18} />
            </button>
            <p className="mt-4 max-w-md text-sm text-soot">
              See what's missing, what your competitors are doing differently,
              and where your profile could improve.
            </p>
          </motion.div>
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
          >
            {[
              { icon: ShieldCheck, label: "No obligation to buy" },
              { icon: Timer, label: "Takes two minutes" },
              { icon: UserCheck, label: "Personalized to your business" },
            ].map((item, i) => (
              <li
                key={i}
                className="flex items-center gap-2 text-sm font-medium text-soot"
              >
                <item.icon size={18} weight="duotone" className="text-pine" />
                {item.label}
              </li>
            ))}
          </motion.ul>
        </div>
        <motion.div
          style={{ y: yVisual }}
          initial={{ opacity: 0, y: 40, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <MapCard />
        </motion.div>
      </div>
    </section>
  );
};
